import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {NotifierService} from "../../../../../../base/services/notifier/notifier.service";
import {EventBusService} from "../../../../../../base/services/eventbus/event-bus.service";
import {IPage, ResponseData} from "../../../../../../base/models/pagination.model";
import {AuthService} from "../../../auth/services/auth.service";
import {BaseService} from "../../../../../../base/services/base.service";
import {IParams} from "../../../../../../base/endpoints/iparams";
import {SocialEndpoint} from "../../social.endpoint";
import {
  INotification,
  INotificationBrief,
  INotificationDetail,
  INotificationReq,
  INotificationResponse
} from "../models/notification.model";
import {IAuth} from "../../../../../common/modules/auth/models/auth.model";

@Injectable({
  providedIn: 'root'
})
export class NotificationService extends BaseService {

  constructor(
    authService: AuthService,
    notifier: NotifierService,
    private http: HttpClient,
    private eventBus: EventBusService
  ) {
    super(authService, notifier);
  }

  searchNotifications(key: string, params: IParams) {
    this.http.get<ResponseData<IPage<INotificationBrief>>>(SocialEndpoint.NOTIFICATIONS.SEARCH(params))
      .subscribe((notifications: ResponseData<IPage<INotificationBrief>>) => {
        this.eventBus.publish(key, notifications.body)
      }, (error: HttpErrorResponse) => {
        this.handleError(error, (auth: IAuth) => {
          this.searchNotifications(key, params)
        }, (err: HttpErrorResponse) => {
          this.eventBus.publish(this.getErrorKey(key), err)
        })
      })
  }

  fetchSingle(key: string, id: number) {
    this.http.get<INotificationDetail>(SocialEndpoint.NOTIFICATIONS.GET(id))
      .subscribe((notification: INotificationDetail) => {
        this.eventBus.publish(key, notification)
      }, (error: HttpErrorResponse) => {
        this.handleError(error, (auth: IAuth) => {
          this.fetchSingle(key, id)
        }, (err: HttpErrorResponse) => {
          this.eventBus.publish(this.getErrorKey(key), err)
        })
      })
  }

  fetchMyNotifications(key: string, params: IParams) {
    this.http.get<ResponseData<IPage<INotification>>>(SocialEndpoint.NOTIFICATIONS.FETCH_MY_NOTIFICATIONS(params))
      .subscribe((notifications: ResponseData<IPage<INotification>>) => {
        this.eventBus.publish(key, notifications.body)
      }, (error: HttpErrorResponse) => {
        this.handleError(error, (auth: IAuth) => {
          this.fetchMyNotifications(key, params)
        }, (err: HttpErrorResponse) => {
          this.eventBus.publish(this.getErrorKey(key), err)
        })
      })
  }

  markRead(key: string, countKey: string, notificationId: number, read: boolean) {
    this.http.post(SocialEndpoint.NOTIFICATIONS.MARK_READ({id: notificationId, read: read}), null)
      .subscribe(res => {
        this.eventBus.publish(key, res)
        this.eventBus.publish(countKey, res) // trigger notification count call
      }, (err: HttpErrorResponse) => {
        this.handleError(err, (auth: IAuth) => {
          this.markRead(key, countKey, notificationId, read)
        }, (err: HttpErrorResponse) => {
          this.eventBus.publish(this.getErrorKey(key), err)
        })
      })
  }

  notify(key: string, notificationId: number, params: IParams) {
    this.http.post(SocialEndpoint.NOTIFICATIONS.NOTIFY(notificationId, params), null)
      .subscribe(res => {
        this.eventBus.publish(key, res)
      }, (err: HttpErrorResponse) => {
        this.handleError(err, (auth: IAuth) => {
        }, (err: HttpErrorResponse) => {
          this.eventBus.publish(this.getErrorKey(key), err)
        })
      })
  }

  makeSticky(key: string, notificationId: number, sticky: boolean) {
    this.http.post(SocialEndpoint.NOTIFICATIONS.MAKE_STICKY({id: notificationId, sticky: sticky}), null)
      .subscribe(res => {
        this.eventBus.publish(key, res)
      }, (err: HttpErrorResponse) => {
        this.handleError(err, (auth: IAuth) => {
          this.makeSticky(key, notificationId, sticky)
        }, (err: HttpErrorResponse) => {
          this.eventBus.publish(this.getErrorKey(key), err)
        })
      })
  }

  publish(key: string, notificationId: number, published: boolean) {
    this.http.post(SocialEndpoint.NOTIFICATIONS.PUBLISH(notificationId, published), null)
      .subscribe(res => {
        this.eventBus.publish(key, res)
      }, (err: HttpErrorResponse) => {
        this.handleError(err, (auth: IAuth) => {
          this.makeSticky(key, notificationId, published)
        }, (err: HttpErrorResponse) => {
          this.eventBus.publish(this.getErrorKey(key), err)
        })
      })
  }

  respond(key: string, notificationId: number, response: INotificationResponse) {
    this.http.post(SocialEndpoint.NOTIFICATIONS.RESPOND(notificationId), response)
      .subscribe(res => {
        this.eventBus.publish(key, res)
      }, (error: HttpErrorResponse) => {
        this.handleError(error, (auth: IAuth) => {
          this.respond(key, notificationId, response)
        }, (error: HttpErrorResponse) => {
          this.eventBus.publish(this.getErrorKey(key), error)
        })
      })
  }

  countUnread(key: string) {
    this.http.get<number>(SocialEndpoint.NOTIFICATIONS.COUNT_UNREAD({}))
      .subscribe(res => {
        this.eventBus.publish(key, res)
      }, (error: HttpErrorResponse) => {
        this.handleError(error, (auth: IAuth) => {
          this.countUnread(key)
        }, (error: HttpErrorResponse) => {
          this.eventBus.publish(this.getErrorKey(key), error)
        })
      })
  }

  increaseViewCount(key: string, id: number) {
    this.http.patch(SocialEndpoint.NOTIFICATIONS.INCREASE_VIEW_COUNT(id), {})
      .subscribe(res => {
        this.eventBus.publish(key, res)
      }, (error: HttpErrorResponse) => {
        this.handleError(error, (auth: IAuth) => {
          this.increaseViewCount(key, id)
        }, (error: HttpErrorResponse) => {
          this.eventBus.publish(this.getErrorKey(key), error)
        })
      })
  }

  increaseClickCount(key: string, id: number) {
    this.http.patch(SocialEndpoint.NOTIFICATIONS.INCREASE_CLICK_COUNT(id), {})
      .subscribe(res => {
        this.eventBus.publish(key, res)
      }, (error: HttpErrorResponse) => {
        this.handleError(error, (auth: IAuth) => {
          this.increaseClickCount(key, id)
        }, (error: HttpErrorResponse) => {
          this.eventBus.publish(this.getErrorKey(key), error)
        })
      })
  }

  upsert(key: string, notificationReq: INotificationReq, exId: number | null) {
    let endpoint = exId == null ? SocialEndpoint.NOTIFICATIONS.CREATE() : SocialEndpoint.NOTIFICATIONS.UPDATE(exId)
    let observable = exId == null ? this.http.post<number>(endpoint, notificationReq)
      : this.http.patch(endpoint, notificationReq)
    observable.subscribe(res => {
      this.eventBus.publish(key, res)
    }, (error: HttpErrorResponse) => {
      this.handleError(error, (auth: IAuth) => {
        this.upsert(key, notificationReq, exId)
      }, (error: HttpErrorResponse) => {
        this.eventBus.publish(this.getErrorKey(key), error)
      })
    })
  }
}
